import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import API_BASE_URL from '../apiConfig';
import './WithdrawStarsPage.css';
import { TonConnectUIProvider, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { commonIcons } from './assetImports';
import { useTranslation } from 'react-i18next';

const WithdrawStarsPageComponent = ({ isTelegramMiniApp, setWallets }) => {
  const { user, updateUserDetails } = useContext(AuthContext);
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const { t } = useTranslation('depositWithdrawAll');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isConnectingWallet, setIsConnectingWallet] = useState(false);
  const navigate = useNavigate();

  const starBalance = user?.wallets?.stars || 0;
  const maxSteps = Math.floor(starBalance / 100);
  const withdrawOptions = Array.from({ length: maxSteps }, (_, i) => (i + 1) * 100);
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [showWalletPopup, setShowWalletPopup] = useState(false);

  useEffect(() => {
    if (wallet?.account?.address) {
      if (!user?.tonWalletAddress || user.tonWalletAddress !== wallet.account.address) {
        saveWalletAddress(wallet.account.address);
      }
    } else if (user?.tonWalletAddress) {
      handleDisconnectWallet();
    }
  }, [wallet, user?.tonWalletAddress]);

  const saveWalletAddress = async (address) => {
    if (!address || address === user?.tonWalletAddress) return;
    try {
      const response = await apiRequest(`${API_BASE_URL}/api/save-ton-wallet`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tonWalletAddress: address })
      });

      if (!response.ok) {
        throw new Error(t('withdrawStars.walletSaveFailed'));
      }

      setMessage('');
      await updateUserDetails();
      
    } catch (error) {
      console.error('Error saving wallet address:', error);
      setMessage(t('withdrawStars.walletSaveFailed'));
    }
  };

  const connectTonWallet = async () => {
    setIsConnectingWallet(true);
    setMessage(t('withdrawStars.connectingWallet'));
    
    try {
      if (tonConnectUI.connected) {
        return true;
      }

      await tonConnectUI.connectWallet();
      return true;
    } catch (error) {
      console.error('Error connecting TON wallet:', error);
      setMessage(t('withdrawStars.walletConnectFailed'));
      return false;
    } finally {
      setIsConnectingWallet(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAmount < 1000) {
      setMessage(t('withdrawStars.minAmountError'));
      const button = document.querySelector(".withdrawStars-submit-button");
      button.classList.add("withdrawStars-error-button");
      setTimeout(() => {
        button.classList.remove("withdrawStars-error-button");
        setMessage('');
      }, 3000);
      return;
    }

    if (!user?.tonWalletAddress) {
      setMessage(t('withdrawStars.connectWalletFirst'));
      const connected = await connectTonWallet();
      if (!connected) return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      const response = await apiRequest(`${API_BASE_URL}/api/request-stars-withdraw`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: selectedAmount })
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.message || t('withdrawStars.withdrawRequestFailed'));
        setIsProcessing(false);
        return;
      }

      setIsProcessing(false);
      setWallets(data.wallets);
      await updateUserDetails();
      setMessage('');

    } catch (error) {
      console.error('Error requesting withdrawal:', error);
      setMessage(t('withdrawStars.withdrawProcessingError'));
      setIsProcessing(false);
    }
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    if (!clientX) return;

    const sliderTrack = document.querySelector(".withdrawStars-slider-track");
    const newAmount = calculateSelectedAmount(clientX, sliderTrack);
    setSelectedAmount(withdrawOptions[newAmount]);
  };

  const calculateSelectedAmount = (clientX, trackElement) => {
    const rect = trackElement.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const trackWidth = rect.width;
    const percent = (clickX / trackWidth) * 100;
    const adjustedPercent = (percent - 5) / (90 / (withdrawOptions.length - 1));
    return Math.round(Math.min(Math.max(adjustedPercent, 0), withdrawOptions.length - 1));
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const clientX = e.clientX || e.touches?.[0]?.clientX;
      if (!clientX) return;

      const sliderTrack = document.querySelector(".withdrawStars-slider-track");
      const newAmount = calculateSelectedAmount(clientX, sliderTrack);
      setSelectedAmount(withdrawOptions[newAmount]);
    };

    const handleEnd = () => setIsDragging(false);

    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd, { passive: false });
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);

    return () => {
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
    };
  }, [isDragging, withdrawOptions]);

  useEffect(() => {
    const preventScroll = (e) => e.preventDefault();

    if (isTelegramMiniApp) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventScroll, { passive: false });
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isTelegramMiniApp]);

  const handleDisconnectWallet = async () => {
    try {
      await tonConnectUI.disconnect();

      const response = await apiRequest(`${API_BASE_URL}/api/disconnect-ton-wallet`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();  

      if (!data.success) throw new Error(data.message); 

      setShowWalletPopup(false);
      await updateUserDetails();
      setMessage(t('withdrawStars.walletDisconnected'));
      setTimeout(() => {
        setMessage('');
      }, 2000);

    } catch (error) {
      setShowWalletPopup(false);
    }
  };

  return (
    <div className="withdrawStars-page-container">
      <h2 className="withdrawStars-page-title">{t('getReward')}</h2>

      {(withdrawOptions.length === 0 && !user?.pendingWithdrawals?.length > 0) ? (
        <p className="withdrawStars-message">{t('withdrawStars.notEnoughStars')}</p>
      ) : (
        <>
          {user?.tonWalletAddress && (
            <>
              <div 
                className="withdrawStars-wallet-connected clickable"
                onClick={() => setShowWalletPopup(true)}
              >
                <span>{t('withdrawStars.connectedWallet')}:</span>
                <span className="withdrawStars-wallet-address">
                  {user.tonWalletAddress.substring(0, 6)}...{user.tonWalletAddress.substring(user.tonWalletAddress.length - 4)}
                </span>
              </div>

              {showWalletPopup && (
                <div className="withdrawStars-address-popup-overlay" onClick={() => setShowWalletPopup(false)}>
                  <div className="withdrawStars-address-popup" onClick={(e) => e.stopPropagation()}>
                    <h4>{user.tonWalletAddress}</h4>
                    <button 
                      className="base-item-box withdrawStars-disconnect-button" 
                      onClick={handleDisconnectWallet}
                    >
                      {t('withdrawStars.disconnect')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="withdrawStars-amount-display">
            <img src={commonIcons.starIcon} alt={t('stars')} className="withdrawStars-currency-icon" />
            <span className="withdrawStars-selected-amount">{selectedAmount}</span>
            <span className="withdrawStars-divider">/ {starBalance}</span>
            <span className="withdrawStars-selected-amount"> =</span>
            <img src={commonIcons.usdtIcon} alt="USDT" className="withdrawStars-currency-icon" />
            <span className="withdrawStars-usdt-amount">{(selectedAmount / 100).toFixed(2)}</span>
          </div>

          <form onSubmit={handleSubmit} className="withdrawStars-form">
            <div className={`withdrawStars-slider-container ${user.wallets.stars < 100 ? 'disabled' : ''}`}>
              <div
                className="withdrawStars-slider-track"
                onMouseDown={() => user.wallets.stars >= 100 && setIsDragging(true)}
                onTouchStart={() => user.wallets.stars >= 100 && setIsDragging(true)}
                onPointerUp={user.wallets.stars >= 100 ? handleTrackClick : undefined}
              >
                <div className="withdrawStars-slider-clickable-overlay" />
                <div className="withdrawStars-slider-dots">
                  {withdrawOptions.map((_, index) => (
                    <div key={index} className="withdrawStars-dot"></div>
                  ))}
                </div>
                <div
                  className="withdrawStars-slider-handle"
                  style={{
                    left: withdrawOptions.length === 1 
                      ? '95%' 
                      : `calc(5% + ${(withdrawOptions.indexOf(selectedAmount) * (90 / (withdrawOptions.length - 1)))}%)`,
                    opacity: user.wallets.stars < 100 ? 0.5 : 1
                  }}
                ></div>
              </div>
            </div>

            <button
              type="submit"
              className="base-item-box withdrawStars-submit-button"
              disabled={isProcessing || isConnectingWallet || selectedAmount === 0}
            >
              <span className="base-item-content withdrawStars-submit">
                {isProcessing ? (
                  <>
                    <span className="withdrawStars-spinner"></span> {t('withdrawStars.processing')}
                  </>
                ) : isConnectingWallet ? (
                  <>
                    <span className="withdrawStars-spinner"></span> {t('withdrawStars.connecting')}
                  </>
                ) : t('withdrawStars.submitRequest')}
              </span>
            </button>
            {message && <p className="withdrawStars-message">{message}</p>}

            {user?.pendingWithdrawals?.length > 0 && (
              <div className="withdrawStars-pending-list">
                {user.pendingWithdrawals.map((item, index) => (
                  <div key={index} className="withdrawStars-wallet-connected">
                    <span>
                      {item.amount} {t('stars')} (~{(item.amount / 100).toFixed(2)} USDT) 
                      {t('withdrawStars.requestedOn')} {new Date(item.requestedAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
                <p style={{ marginTop: '10px', fontSize: '12px', color: '#fff' }}>
                  {t('withdrawStars.withdrawProcessingTime')}
                </p>
              </div>
            )}
          </form>
        </>
      )}
    </div>
  );
};

const WithdrawStarsPage = (props) => {
  const manifestUrl = 'https://gamestar24.com/tonconnect-manifest.json';

  return (
    <TonConnectUIProvider manifestUrl={manifestUrl}>
      <WithdrawStarsPageComponent {...props} />
    </TonConnectUIProvider>
  );
};

export default WithdrawStarsPage;